import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useParams } from "react-router-dom";
import { Archive, Download, ArrowLeft } from "lucide-react";
import toast from "react-hot-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { AgentEmailSection } from "@/components/Agents/AgentEmailSection";
import { AgentScheduleSection } from "@/components/Agents/AgentScheduleSection";
import { useAgents } from "@/components/Agents/AgentsContext";
import { useSession } from "@/auth/SessionProvider";
import { agentsHref } from "@/lib/atwAgentsHost";
import { agentDirectory, timelineForAgent, type TimelineEvent } from "@/lib/agentsDemoData";

type AgentTab = "timeline" | "overview" | "configuration";

function kindLabel(kind: TimelineEvent["kind"]): string {
  return kind.replace(/_/g, " ");
}

function resultBadge(result?: TimelineEvent["result"]) {
  if (!result) return null;
  if (result === "success") return <Badge>Success</Badge>;
  if (result === "fail") return <Badge variant="destructive">Fail</Badge>;
  if (result === "stopped") return <Badge variant="secondary">Stopped</Badge>;
  if (result === "partial") return <Badge variant="outline">Partial</Badge>;
  return <Badge variant="outline">Pending</Badge>;
}

const CONFIG_SECTIONS: { title: string; body: string }[] = [
  {
    title: "Connections",
    body: "Systems this agent may use. Org-level connections are managed under Connections. Agent overrides come later.",
  },
  {
    title: "Tools / actions",
    body: "What the agent may do on each connection (update lead, send email, reply ticket). Stub for Phase 1.",
  },
  {
    title: "Knowledge base",
    body: "Author libraries, books, and articles under Knowledge. Assigning that knowledge to this agent comes later on Agent Configuration.",
  },
  {
    title: "Skills",
    body: "Cross-cutting abilities that are not a connected system (example: web browse).",
  },
  {
    title: "Playbook",
    body: "Happy path for the job, kept short for v1.",
  },
  {
    title: "Stops / escalations",
    body: "When work must stop for a human (discount, legal, VIP).",
  },
  {
    title: "Voice",
    body: "Tone, signature, and persona for outbound messages.",
  },
  {
    title: "Human owner",
    body: "Who Overseer notifies on escalations. Must be a user with at least Standard access.",
  },
  {
    title: "Notifications",
    body: "Escalation hooks for this agent. Org-wide prefs live under Settings.",
  },
];

export function AgentDetailPage() {
  const params = useParams();
  const agentId = typeof params?.agentId === "string" ? params.agentId : "";
  const { getAgent, archiveAgent, may, viewOnly, org } = useAgents();
  const session = useSession();
  const agent = getAgent(agentId);
  const [tab, setTab] = useState<AgentTab>("timeline");
  const people = useMemo(
    () => agentDirectory(org.users, session.data?.user),
    [org.users, session.data?.user],
  );

  const events = useMemo(() => (agent ? timelineForAgent(agent.id) : []), [agent]);
  const sharedNames = useMemo(() => {
    if (!agent) return [];
    return (agent.sharedWithUserIds ?? [])
      .filter((id) => id !== agent.humanOwnerUserId)
      .map((id) => people.find((person) => person.id === id)?.name ?? id);
  }, [agent, people]);

  if (!agent) {
    return (
      <div className="space-y-4">
        <Link
          to={agentsHref("/")}
          className="inline-flex items-center gap-1 text-sm text-gray-600 hover:underline"
        >
          <ArrowLeft className="size-4" /> Back to agents
        </Link>
        <p className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900">
          Agent not found.
        </p>
      </div>
    );
  }

  const tabs: { id: AgentTab; label: string }[] = [
    { id: "timeline", label: "Timeline" },
    { id: "overview", label: "Overview" },
    { id: "configuration", label: "Agent Configuration" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to={agentsHref("/")}
            className="mb-2 inline-flex items-center gap-1 text-sm text-gray-600 hover:underline dark:text-gray-400"
          >
            <ArrowLeft className="size-4" /> Agents
          </Link>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{agent.name}</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Owner {agent.humanOwnerName} ({agent.humanOwnerEmail})
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={agent.status === "archived" ? "outline" : "default"}>
            {agent.status === "archived" ? "Archived" : agent.status === "paused" ? "Paused" : "Active"}
          </Badge>
          {agent.status !== "archived" ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!may("create_edit_archive_agents")}
              title={
                viewOnly
                  ? "Standard role is view only"
                  : "Archive keeps history. Agents are not deleted."
              }
              onClick={() => {
                archiveAgent(agent.id);
                toast.success(`${agent.name} archived. History is kept.`);
              }}
            >
              <Archive className="size-4" aria-hidden />
              Archive
            </Button>
          ) : null}
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-gray-200 dark:border-white/10">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`rounded-t-md px-3 py-2 text-sm font-medium ${
              tab === t.id
                ? "border border-b-0 border-gray-200 bg-white text-gray-900 dark:border-white/10 dark:bg-[#1D232D] dark:text-white"
                : "text-gray-600 hover:text-gray-900 dark:text-gray-400"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "timeline" ? (
        <section className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Newest first. Tool calls, stops, and human resolutions stay here.
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                toast.success("Timeline export is a stub. CSV/JSON export will land later.")
              }
            >
              <Download className="size-4" aria-hidden />
              Export
            </Button>
          </div>
          <ul className="space-y-3">
            {events.map((ev) => (
              <li
                key={ev.id}
                id={ev.id}
                className="rounded-lg border border-gray-200 bg-white px-4 py-3 dark:border-white/10 dark:bg-[#1D232D]"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500">
                      {kindLabel(ev.kind)} · {ev.actor}
                      {ev.credits != null ? ` · ${ev.credits} credits` : ""}
                    </p>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">{ev.summary}</p>
                    {ev.externalRefs?.length ? (
                      <p className="mt-1 text-xs text-gray-500">
                        {ev.externalRefs.map((r) => `${r.label} ${r.id}`).join(" · ")}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    {resultBadge(ev.result)}
                    <time className="text-xs text-gray-500" dateTime={ev.at}>
                      {new Date(ev.at).toLocaleString()}
                    </time>
                  </div>
                </div>
              </li>
            ))}
            {events.length === 0 ? (
              <li className="rounded-md border border-dashed px-4 py-8 text-center text-sm text-gray-500">
                No timeline events yet for this agent.
              </li>
            ) : null}
          </ul>
        </section>
      ) : null}

      {tab === "overview" ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Agent</CardTitle>
              <CardDescription>Named worker in this organization</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p>
                <span className="text-muted-foreground">Name:</span> {agent.name}
              </p>
              <p>
                <span className="text-muted-foreground">Owner:</span> {agent.humanOwnerName} (
                {agent.humanOwnerEmail})
              </p>
              <p>
                <span className="text-muted-foreground">Shared with:</span>{" "}
                {sharedNames.length > 0 ? sharedNames.join(", ") : "Only the owner"}
              </p>
              <p>
                <span className="text-muted-foreground">Status:</span> {agent.status}
              </p>
              <p>
                <span className="text-muted-foreground">Credits this month:</span>{" "}
                {agent.creditsBurnedMonth.toLocaleString()}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Organization</CardTitle>
              <CardDescription>{org.name}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p>
                <span className="text-muted-foreground">Plan:</span> {org.planLabel}. Unlimited
                agents. Usage is credits.
              </p>
              <p>
                <span className="text-muted-foreground">Last active:</span>{" "}
                {new Date(agent.lastActiveAt).toLocaleString()}
              </p>
              <p className="text-xs text-muted-foreground">
                Agents are archived, never deleted, so timelines stay available.
              </p>
            </CardContent>
          </Card>
        </div>
      ) : null}

      {tab === "configuration" ? (
        <section className="space-y-4">
          {viewOnly ? (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-100">
              Standard role is view only. A Super Admin can edit Agent Configuration.
            </p>
          ) : null}
          <AgentEmailSection key={agent.id} agent={agent} />
          <AgentScheduleSection key={`${agent.id}-schedules`} agent={agent} />
          <div className="grid gap-3 sm:grid-cols-2">
            {CONFIG_SECTIONS.map((section) => (
              <Card key={section.title}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">{section.title}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm text-muted-foreground">
                  <p>{section.body}</p>
                  {section.title === "Knowledge base" ? (
                    <Link
                      to={agentsHref("/knowledge")}
                      className="inline-flex h-8 items-center rounded-md border border-gray-300 px-3 text-xs font-semibold hover:bg-gray-50 dark:border-white/20"
                    >
                      Open Knowledge
                    </Link>
                  ) : (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={!may("manage_agent_configuration")}
                      onClick={() =>
                        toast.success(`${section.title} editor is a placeholder for Phase 1.`)
                      }
                    >
                      {may("manage_agent_configuration") ? "Edit" : "View only"}
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            Human owner for this agent: {agent.humanOwnerName}.{" "}
            <Link to={agentsHref("/connections")} className="underline underline-offset-2">
              Manage org connections
            </Link>
          </p>
        </section>
      ) : null}
    </div>
  );
}
